import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import Env from '@ridango/core/env';
import { Authenticator } from '@ridango/components';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { LaunchDarklyService } from '@ridango/feature-flags';
import { BaseComponent, FEATURE_FLAGS } from '@ridango/core';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-login-email-form',
    templateUrl: './login-email-form.component.html',
    styleUrls: ['./login-email-form.component.scss']
})
export class LoginEmailFormComponent extends BaseComponent implements OnInit {
    private readonly SHOW_PASSWORD = 'Show password';
    private readonly HIDE_PASSWORD = 'Hide password';
    private readonly REMEMBER_ME = 'Remember me';

    protected hasRecaptcha = false;

    loginForm: FormGroup;

    showPassword = false;

    tokenVisible = false;
    siteKey: string;
    recaptchaValue = '';

    @Input() captchaToggleAllowed = false;

    @Output() onLogin = new EventEmitter<any>();
    @Output() onForgot = new EventEmitter<void>();

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private authService: Authenticator,
        private liveAnnouncer: LiveAnnouncer,
        private translate: TranslateService,
        private analyticsService: AnalyticsService,
        private featureFlagService: LaunchDarklyService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initHasRecaptcha();

        if (this.locale.length !== 2) {
            this.locale = 'en';
        }

        const rememberMeValue = this.authService.getRememberMeValue();

        this.loginForm = new FormGroup({
            email: new FormControl(rememberMeValue || '', [Validators.required, Validators.email]),
            password: new FormControl('', Validators.required),
            rememberMe: new FormControl(!!rememberMeValue)
        });

        if (this.env?.RECAPTCHA?.SITEKEY) {
            this.siteKey = this.env.RECAPTCHA.SITEKEY;
        }
    }

    login() {
        if (this.loginForm.valid) {
            this.onLogin.emit(this.loginForm.value);
        } else {
            this.email.markAsTouched();
            this.password.markAsTouched();
        }
    }

    forgot() {
        this.onForgot.emit();
    }

    togglePassword(event = null) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.liveAnnouncer.announce(
            this.translate.instant(this.showPassword ? 'PASSWORD_IS_HIDDEN' : 'PASSWORD_IS_VISIBLE')
        );
        this.showPassword = !this.showPassword;

        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.LOGIN,
            name: `${this.showPassword ? this.SHOW_PASSWORD : this.HIDE_PASSWORD}`
        });
    }

    resolved(captchaResponse: string) {
        this.recaptchaValue = captchaResponse;
    }

    get email() {
        return this.loginForm.get('email');
    }

    get password() {
        return this.loginForm.get('password');
    }

    get showGreenCheck() {
        return this.env.APP_NAME === 'gotland';
    }

    onRememberMeChanged() {
        const rememberMe: boolean = this.loginForm.get('rememberMe').value;
        const eventAction = rememberMe ? TrackedEventAction.CHECKED : TrackedEventAction.UNCHECKED;

        this.analyticsService.eventTrack(eventAction, {
            category: TrackedEventCategory.LOGIN,
            name: this.REMEMBER_ME
        });
    }

    private initHasRecaptcha(): void {
        this.featureFlagService
            .getFlags(FEATURE_FLAGS.FrontendRecaptcha)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((flagValue: boolean) => (this.hasRecaptcha = flagValue || !this.captchaToggleAllowed));
    }
}
